// Central product catalogue. Every field is either a real value or the
// literal "Contact us for technical information." — DO NOT invent
// technical values.

const CONTACT = "Contact us for technical information.";
const SUBSTRATE_FALLBACK =
  "Compatibility depends on substrate, machine and production application. Please share your requirement for review.";

const APPLICATION_REVIEW_PROMPTS = [
  "Machine make/model",
  "Substrate",
  "Current ink",
  "Print requirement",
  "Production speed",
  "Existing problem",
];

const CATEGORY_LABELS = {
  blister: "Blister Printing",
  specialty_ink: "Specialty Printing Ink",
  aid: "Printing Aid",
  coding: "Coding Consumable",
  cleaning: "Maintenance Product",
  production: "Production Consumable",
};

// Solution grouping (used by solution pages to filter products).
const SOLUTION_OF = {
  blister: "blister-printing",
  specialty_ink: "specialty-inks",
  aid: "printing-aids",
  coding: "coding-marking",
  cleaning: "printing-aids",
  production: "production-consumables",
};

const defaultCharacteristics = () => ({
  colour: CONTACT,
  drying: CONTACT,
  recommendedSubstrate: CONTACT,
  printingProcess: CONTACT,
  storage: CONTACT,
  packSizes: CONTACT,
});

// Image placeholder mapping — tagged so real product photography can
// replace these later. All paths are self-hosted from /images/home/.
const PLACEHOLDER = {
  blister: "sample-flatlay",
  coding: "problem-tij-coding",
  other: "sample-flatlay",
};

export const products = [
  {
    slug: "blister-pack-violet-ink",
    name: "Blister Pack Violet Ink",
    pageH1: "Blister Pack Violet Ink for Pharmaceutical Printing Applications",
    pageIntro:
      "A specialist violet printing ink supplied for appropriate pharmaceutical blister-pack printing applications.",
    category: "blister",
    shortDescription:
      "Violet printing ink developed for pharmaceutical blister-pack applications, supplied subject to application testing under production conditions.",
    typicalApplications: [
      "Pharmaceutical blister-pack printing",
      "Batch and lot identification on blister foil",
      "Production-line printing on aluminium blister substrates",
    ],
    seoDescription:
      "Violet printing ink for pharmaceutical blister-pack applications, supplied internationally from Dubai subject to application testing.",
    heroImageKey: PLACEHOLDER.blister,
    heroAlt:
      "Pharmaceutical printing samples including aluminium blister foil",
  },
  {
    slug: "blister-pack-inks-aid",
    name: "Blister Pack Inks-aid",
    category: "blister",
    shortDescription:
      "Supporting product formulated for use alongside blister printing inks in appropriate pharmaceutical applications; suitability confirmed through application review.",
    typicalApplications: [
      "Support product for blister printing ink workflows",
      "Use in pharmaceutical blister packaging lines",
    ],
    seoDescription:
      "Supporting product for pharmaceutical blister-pack printing workflows, supplied internationally from Dubai subject to review.",
    heroImageKey: PLACEHOLDER.blister,
    heroAlt: "Blister-pack printing samples on a technical flat-lay",
  },
  {
    slug: "indelible-ink",
    name: "Indelible Ink",
    category: "specialty_ink",
    shortDescription:
      "Indelible printing ink developed for identification and marking applications requiring durable print performance, supplied subject to application testing.",
    typicalApplications: [
      "Durable identification marking",
      "Selected industrial substrates requiring indelible print",
      "Applications where print permanence is required",
    ],
    seoDescription:
      "Indelible printing ink for identification and marking applications, supplied internationally from Dubai subject to application testing.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Industrial ink swatches on a technical flat-lay",
  },
  {
    slug: "indelible-poly-inks-aid",
    name: "Indelible Poly Inks-aid",
    category: "aid",
    shortDescription:
      "Supporting product for use with indelible printing on selected polymer substrates in industrial applications; final suitability confirmed through application review.",
    typicalApplications: [
      "Support product used with indelible ink workflows",
      "Applications involving selected polymer substrates",
    ],
    seoDescription:
      "Supporting product for indelible printing on selected polymer substrates, supplied internationally from Dubai subject to review.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Printing aid samples on a technical flat-lay",
  },
  {
    slug: "porous-ink",
    name: "Porous Ink",
    category: "specialty_ink",
    shortDescription:
      "Printing ink developed for use on porous substrates in production environments requiring reliable identification, supplied subject to application testing.",
    typicalApplications: [
      "Printing on porous substrates such as paperboard cartons",
      "Batch and lot identification on absorbent surfaces",
      "Production-line coding of porous materials",
    ],
    seoDescription:
      "Printing ink for porous substrates in production environments, supplied internationally from Dubai subject to application testing.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Coded pharmaceutical carton and ink swatches",
  },
  {
    slug: "non-porous-ink",
    name: "Non-Porous Ink",
    category: "specialty_ink",
    shortDescription:
      "Printing ink developed for use on non-porous substrates in production environments where consistent print performance is required; suitability confirmed through application review.",
    typicalApplications: [
      "Printing on non-porous substrates such as film and coated surfaces",
      "Identification on sealed or laminated packaging",
      "Production-line coding of non-absorbent materials",
    ],
    seoDescription:
      "Printing ink for non-porous substrates in production environments, supplied internationally from Dubai subject to review.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Industrial printing samples on non-porous substrates",
  },
  {
    slug: "pvc-reducer",
    name: "PVC Reducer",
    category: "aid",
    shortDescription:
      "Reducer product for use with selected PVC-based printing ink applications, supplied to industrial customers subject to application testing.",
    typicalApplications: [
      "Support product used with PVC-based printing inks",
      "Selected industrial printing applications",
    ],
    seoDescription:
      "Reducer for selected PVC-based printing ink applications, supplied internationally from Dubai subject to application testing.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Industrial printing aid samples on a technical flat-lay",
  },
  {
    slug: "ss-polyfine-inks-aid",
    name: "SS Polyfine Inks-aid",
    category: "aid",
    shortDescription:
      "Supporting product for the SS Polyfine printing ink range in relevant industrial applications; final suitability confirmed through application review.",
    typicalApplications: [
      "Support product used with SS Polyfine printing inks",
      "Selected industrial and pharmaceutical printing applications",
    ],
    seoDescription:
      "Supporting product for the SS Polyfine ink range in industrial applications, supplied internationally from Dubai subject to review.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Industrial printing aid samples on a technical flat-lay",
  },
  {
    slug: "screen-cleaning-aid",
    name: "Screen Cleaning Aid",
    category: "cleaning",
    shortDescription:
      "Cleaning product formulated for screen cleaning and maintenance tasks on printing lines, supplied subject to application testing.",
    typicalApplications: [
      "Screen cleaning between production runs",
      "Print-station maintenance workflows",
      "Support of printing-line consumable programmes",
    ],
    seoDescription:
      "Screen cleaning product for printing-line maintenance, supplied internationally from Dubai subject to application testing.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Screen cleaning consumables on a technical flat-lay",
  },
  {
    slug: "metal-stereos",
    name: "Metal Stereos",
    category: "production",
    shortDescription:
      "Metal stereo plates produced for pharmaceutical printing lines requiring the production of dedicated print units.",
    typicalApplications: [
      "Dedicated print units for pharmaceutical printing lines",
      "Support of blister and carton printing workflows",
    ],
    seoDescription:
      "Metal stereo plates for pharmaceutical printing lines, supplied internationally from Dubai subject to application review.",
    heroImageKey: PLACEHOLDER.other,
    heroAlt: "Metal stereo plate on a printing technical flat-lay",
  },
  {
    slug: "solvent-hp-compatible-cartridge",
    name: "Solvent HP-Compatible Cartridge",
    category: "coding",
    shortDescription:
      "Solvent-based cartridge developed for compatibility with selected HP-style thermal inkjet coding printers, supplied subject to application testing.",
    typicalApplications: [
      "Coding on non-porous substrates in production environments",
      "Batch, expiry and lot identification via HP-style TIJ printers",
      "Variable-information printing on selected packaging surfaces",
    ],
    seoDescription:
      "Solvent HP-compatible thermal inkjet cartridge for production coding on non-porous substrates, supplied internationally from Dubai.",
    heroImageKey: PLACEHOLDER.coding,
    heroAlt: "Thermal inkjet printer coding a pharmaceutical carton",
  },
  {
    slug: "water-based-hp-black-cartridge",
    name: "Water-Based HP Black Cartridge",
    category: "coding",
    shortDescription:
      "Water-based black cartridge developed for compatibility with selected HP-style thermal inkjet coding printers on porous substrates; suitability confirmed through application review.",
    typicalApplications: [
      "Coding on porous substrates such as paperboard cartons",
      "Batch, expiry and lot identification via HP-style TIJ printers",
      "Variable-information printing on porous packaging surfaces",
    ],
    seoDescription:
      "Water-based HP-compatible black thermal inkjet cartridge for coding on porous substrates, supplied internationally from Dubai.",
    heroImageKey: PLACEHOLDER.coding,
    heroAlt:
      "Thermal inkjet printer coding batch and expiry data on a carton",
  },
].map((p) => ({
  ...p,
  seoTitle: `${p.name} | Axion Group`,
  categoryLabel: CATEGORY_LABELS[p.category],
  solutionSlug: SOLUTION_OF[p.category],
  substrates: SUBSTRATE_FALLBACK,
  characteristics: defaultCharacteristics(),
  applicationReviewPrompts: APPLICATION_REVIEW_PROMPTS,
}));

// Non-detail production consumables (combined page only — no individual pages).
export const combinedProductionItems = [
  {
    slug: "double-sided-tape",
    name: "Double-Sided Tape",
    iconKey: "Package",
    desc: "General-purpose double-sided tape for pharmaceutical production and packaging workflows.",
  },
  {
    slug: "double-sided-foam-tape",
    name: "Double-Sided Foam Tape",
    iconKey: "Package",
    desc: "Cushioned double-sided foam tape for fixture and packaging applications on production lines.",
  },
  {
    slug: "latex-examination-gloves",
    name: "Latex Examination Gloves",
    subnote: "Powder-Free & Powdered variants available",
    iconKey: "Hand",
    desc: "Latex examination gloves for pharmaceutical production and handling environments.",
  },
  {
    slug: "bouffant-caps",
    name: "Bouffant Caps",
    iconKey: "Crown",
    desc: "Disposable bouffant caps for pharmaceutical clean-area workflows.",
  },
  {
    slug: "3-ply-masks",
    name: "3-Ply Masks",
    iconKey: "ShieldCheck",
    desc: "Disposable 3-ply masks for pharmaceutical production areas.",
  },
  {
    slug: "shoe-covers",
    name: "Shoe Covers",
    iconKey: "Footprints",
    desc: "Disposable shoe covers for pharmaceutical clean-area entry.",
  },
  {
    slug: "disposable-aprons",
    name: "Disposable Aprons",
    iconKey: "Shirt",
    desc: "Disposable aprons for pharmaceutical production and handling workflows.",
  },
  {
    slug: "disposable-pants",
    name: "Disposable Pants",
    iconKey: "Shirt",
    desc: "Disposable pants for pharmaceutical clean-area workflows.",
  },
];

export const findProduct = (slug) => products.find((p) => p.slug === slug);

export const productsByCategory = (category) =>
  products.filter((p) => p.category === category);

export const productsBySolutionSlug = (solutionSlug) =>
  products.filter((p) => p.solutionSlug === solutionSlug);

// Return 3 related products: same category first, then adjacent categories,
// excluding the current product.
export const relatedProducts = (slug) => {
  const current = findProduct(slug);
  if (!current) return [];
  const sameCat = products.filter(
    (p) => p.category === current.category && p.slug !== slug
  );
  const others = products.filter(
    (p) => p.category !== current.category && p.slug !== slug
  );
  return [...sameCat, ...others].slice(0, 3);
};

export const CHARACTERISTIC_FIELDS = [
  { key: "colour", label: "Colour" },
  { key: "drying", label: "Drying" },
  { key: "recommendedSubstrate", label: "Recommended Substrate" },
  { key: "printingProcess", label: "Printing Process" },
  { key: "storage", label: "Storage" },
  { key: "packSizes", label: "Pack Sizes" },
];

export { CATEGORY_LABELS };
